'use client';

import { useEffect, useRef } from 'react';
import { useServerInsertedHTML } from 'next/navigation';
import { OPTIMIZELY_SCRIPT_ID, OPTIMIZELY_SCRIPT_SRC } from './constants';

const ANALYTICS_NEXT_UMD_VERSION = process.env.NEXT_PUBLIC_ANALYTICS_NEXT_UMD_VERSION ?? '1.81.1';
const ANALYTICS_NEXT_UMD_ORIGIN = 'https://cdn.jsdelivr.net';
const ANALYTICS_NEXT_UMD_URL = `https://cdn.jsdelivr.net/npm/@segment/analytics-next@${ANALYTICS_NEXT_UMD_VERSION}/dist/umd/index.js`;
const SEGMENT_UMD_SCRIPT_ID = 'segment-analytics-next-umd';
const SEGMENT_BOOTSTRAP_SCRIPT_ID = 'segment-bootstrap';
const SEGMENT_BOOTSTRAP_LISTENER_ATTRIBUTE = 'data-segment-bootstrap-listener';

const segmentBootstrapWarningFn = `function logSegmentBootstrapWarning(reason){try{if(typeof console!=="undefined"&&console.warn)console.warn("[segment-early]",reason&&reason.message?reason.message:reason);}catch(_logErr){}}`;

function buildInlineSegmentBootstrap(segmentKey: string) {
  return `
(function(){
  ${segmentBootstrapWarningFn}
  try{
    if(typeof window==="undefined"||typeof document==="undefined"||!document.head)return;
    window.__SEGMENT_WRITE_KEY__=${JSON.stringify(segmentKey)};

    function ensureSegmentScript(){
      var existingScript=document.getElementById(${JSON.stringify(SEGMENT_UMD_SCRIPT_ID)});
      if(existingScript)return existingScript;

      var createdScript=document.createElement("script");
      createdScript.async=true;
      createdScript.crossOrigin="anonymous";
      createdScript.id=${JSON.stringify(SEGMENT_UMD_SCRIPT_ID)};
      createdScript.src=${JSON.stringify(ANALYTICS_NEXT_UMD_URL)};
      document.head.appendChild(createdScript);

      return createdScript;
    }

    function bootstrapSegment(){
      try{
        var AnalyticsNext=window.AnalyticsNext;
        if(!AnalyticsNext||!AnalyticsNext.AnalyticsBrowser||!window.__SEGMENT_WRITE_KEY__)return false;
        var existing=window.analytics;
        if(!existing||typeof existing.user!=="function"){
          try{
            window.analytics=AnalyticsNext.AnalyticsBrowser.load({writeKey:window.__SEGMENT_WRITE_KEY__});
          }catch(loadError){
            logSegmentBootstrapWarning(loadError);
            return true;
          }
        }
        if(window.analytics&&typeof window.analytics.then==="function"){
          window.analytics.then(undefined,logSegmentBootstrapWarning);
        }
        return true;
      }catch(bootstrapError){
        logSegmentBootstrapWarning(bootstrapError);
        return true;
      }
    }

    if(bootstrapSegment())return;

    var segmentScript=ensureSegmentScript();
    if(segmentScript.getAttribute(${JSON.stringify(SEGMENT_BOOTSTRAP_LISTENER_ATTRIBUTE)})==="true")return;

    segmentScript.setAttribute(${JSON.stringify(SEGMENT_BOOTSTRAP_LISTENER_ATTRIBUTE)},"true");
    segmentScript.addEventListener("load",bootstrapSegment,{once:true});
    segmentScript.addEventListener("error",function(){try{logSegmentBootstrapWarning("segment-umd-load-failed");}catch(_onErr){}},{once:true});
  }catch(outerErr){logSegmentBootstrapWarning(outerErr);}
})();
`.trim();
}

function useOptimizelyScript() {
  const injected = useRef(false);

  useEffect(() => {
    if (injected.current) return;
    injected.current = true;

    if (document.getElementById(OPTIMIZELY_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = OPTIMIZELY_SCRIPT_ID;
    script.src = OPTIMIZELY_SCRIPT_SRC;
    script.async = true;
    document.head.appendChild(script);
  }, []);
}

export function SegmentBootstrappedOptimizelyScript({ segmentKey }: { segmentKey: string }) {
  const inserted = useRef(false);
  const inlineScript = buildInlineSegmentBootstrap(segmentKey);

  useServerInsertedHTML(() => {
    if (inserted.current) return null;
    inserted.current = true;
    return (
      <>
        <link href={ANALYTICS_NEXT_UMD_ORIGIN} rel="dns-prefetch" />
        <link crossOrigin="" href={ANALYTICS_NEXT_UMD_ORIGIN} rel="preconnect" />
        <link as="script" crossOrigin="" href={ANALYTICS_NEXT_UMD_URL} rel="preload" />
        <link as="script" href={OPTIMIZELY_SCRIPT_SRC} rel="preload" />
        <script
          // eslint-disable-next-line benzinga/no-unsanitized-innerhtml
          dangerouslySetInnerHTML={{ __html: inlineScript }}
          id={SEGMENT_BOOTSTRAP_SCRIPT_ID}
        />
      </>
    );
  });

  useOptimizelyScript();

  return null;
}
